import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { slide as Menu } from 'react-burger-menu'
import { AuthAction } from '../../actions/AuthAction';
import { history } from '../../history';
import { ProgramConstant } from '../../actions/constants/ProgramConstant';
import { Translate as t } from '../../config/Translate';
import { SparePartsAction } from '../../actions/SparePartsAction';
import { UserType } from '../../config';
import { getLicenseRole } from '../../lib/licenseHelper';
import { canSeeRayDataMenu } from '../../lib/rbacGuard';
import { collectOrderableCompanyIds, resolveSparePartsAccess, getSparePartsMenuLink } from '../../lib/sparePartsAccess';
import { ROLE_EXTERNAL_DEALER } from '../../config/appConstants';
import { ROUTES } from '../../config/routes';
import { ListStateAction } from '../../actions/ListStateAction';
import { LINK_STATE_KEY_MAP } from '../../config/listStateKeys';

const AdminLeftMenu = ({ isOpen, Logout, su, groupList, GetGroupList, user }) => {
    const dispatch = useDispatch();
    // PR #185 R2 Critical 버그 수정 — 기존 코드는 매 render 마다 localStorage 를 직접 read 했으나
    // logout 후 다른 user 로 재로그인 시 React 가 변경을 감지하지 못해 첫 사용자의 메뉴가 stale 유지되어
    // ExternalDealer 가 admin 메뉴를 그대로 보는 RBAC 우회 가능성 발생.
    // → Redux store(state.AuthReducer.user) 를 props 로 받아 user 변화 시 자동 re-render 보장.
    // localStorage 는 fallback 으로만 사용 (e.g. 새 탭 진입 직후 Redux 가 비어있는 순간).
    const uData = user ?? (() => {
        try {
            const raw = localStorage.getItem('user');
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    })();

    const [menuOpen, setMenuOpen] = useState(false);
    const [rayDataTitle, setRayDataTitle] = useState(t('product.label.rayData'));
    const [orderableCompany, setOrderableCompany] = useState([]);


    useEffect(() => {
        if (groupList.length == 0)
            GetGroupList();
    }, []);

    useEffect(() => {
        if (uData?.companyId === 'RAY02091') {
            setRayDataTitle('MegagenData');
        }
    }, [uData?.companyId]);

    useEffect(() => {
        setMenuOpen(isOpen);
    }, [isOpen]);


    useEffect(() => {
        setOrderableCompany(collectOrderableCompanyIds(groupList));
    }, [groupList])
    
    const goMenu = (menu) => {
        dispatch({ type: ProgramConstant.SIDEMENU_CLOSE })
        const sk = LINK_STATE_KEY_MAP[menu.link];
        if (sk) dispatch(ListStateAction.Clear(sk));
        history.push(menu.link);
    }

    const goLinkmenu = (menu) => {
        dispatch({ type: ProgramConstant.SIDEMENU_CLOSE })
        const sk = LINK_STATE_KEY_MAP[menu.listlink];
        if (sk) dispatch(ListStateAction.Clear(sk));
        history.push(menu.listlink);
    }

    const closeMN = () => {
        dispatch({ type: ProgramConstant.SIDEMENU_CLOSE })
    }

    const handleStateChange = (state) => {
        if (!state.isOpen)
            dispatch({ type: ProgramConstant.SIDEMENU_CLOSE })
    }

    const goTech = () => {
        dispatch({ type: ProgramConstant.SIDEMENU_CLOSE })
        window.location = "/";
    }

    // FEAT-025 SPEC-RG-04 — External Dealer 는 SmartDent 라이선스 메뉴만 노출.
    // 기존 menu1/menu2 (RAY 장비/서비스/사이트 등) 전체 숨김. 메뉴 분기 SSoT 는 getLicenseRole.
    const isExternalDealer = getLicenseRole(uData) === ROLE_EXTERNAL_DEALER;

    const menu1 = isExternalDealer ? [
        { link: "/Admin/License", title: t('license.title'), icon: "business-outline" },
    ] : [
        { link: "/Admin/AdminProductDashboard", title: t('product.title'), icon: "laptop-outline", listlink: "/Admin/AdminProductList" },
        { link: "/Admin/AdminServiceDashboard", title: t('service.title'), icon: "key-outline", listlink: "/Admin/AdminServiceList" },
        { link: "/Admin/AdminErrorDashboard", title: t('common.label.errors'), icon: "bug-outline", listlink: "/Admin/AdminErrorList" },
        { link: "/Admin/AdminSiteDashboard", title: t('site.title'), icon: "navigate-outline", listlink: "/Admin/AdminSiteList" },
        { link: "/Admin/AdminWarrantyDashboard", title: t('warranty.title'), icon: "server-outline" },
        { link: "/Admin/AdminSiteSurveyList", title: t('installation.label.siteSurvey'), icon: "reader-outline" },
        { link: "/Admin/AdminInstallationList", title: t('installation.label.installReport'), icon: "file-tray-stacked-outline" },
        { link: "/Admin/AdminCalendar", title: t('installation.calendar.title'), icon: "calendar" },
    ]
    const menu2 = isExternalDealer ? [] : [
        { link: "/Admin/AdminProductStockList", title: t('product.label.stock'), icon: "layers-outline" },
        { link: "/Admin/AdminCustomFieldList", title: t('common.label.customField'), icon: "bookmark-outline" },
        { link: "/Admin/AdminUserList", title: t('user.title'), icon: "navigate-outline" },
        { link: "/Admin/AdminCompanyList", title: t('common.label.company'), icon: "business-outline" },

        { link: "/Admin/AdminErrorCodeList", title: t('product.field.errorCode'), icon: "receipt-outline", suonly: false },
        { link: "/Admin/AdminRayDataList", title: rayDataTitle, icon: "server-outline", suonly: true },
        { link: "/Admin/AdminRayCode", title: t('product.label.rayCode'), icon: "server-outline", suonly: true },
        { link: "/Admin/AdminProductPart", title: t('product.label.productPart'), icon: "hammer-outline", suonly: true },
        { link: "/Admin/AdminTranslate", title: t('admin.translate.title'), icon: "sync-outline", suonly: true },
    ]

    // Spare Parts 메뉴는 하나로 합침 — 본사 권한이면 본사 화면, 딜러 권한이면 딜러 화면으로 진입(탭으로 이동).
    // External Dealer 는 Spare Parts 등 부가 메뉴도 노출 안 함.
    const sparePartsAccess = resolveSparePartsAccess({ user: uData, isExternalDealer, orderableIds: orderableCompany });
    if (sparePartsAccess.isHQ || sparePartsAccess.isDealer)
        menu1.push({ link: getSparePartsMenuLink(sparePartsAccess), title: t('admin.menu.spareParts'), icon: "hammer-outline" });

    // IMP-388 FR-008: 라이선스 역할이 있는 비-외부딜러(HQ·법인 등)도 라이선스 허브로 진입.
    if (!isExternalDealer && getLicenseRole(uData) !== null)
        menu1.push({ link: "/Admin/License", title: t('license.title'), icon: "business-outline" });

    // 외부 딜러는 menu2 가 비어 있음 — 빈 '데이터' 제목만 남지 않도록 필터 결과가 있을 때만 묶음을 그린다.
    const visibleMenu2 = menu2.filter(m => (!m.suonly || (m.link === '/Admin/AdminRayDataList' && canSeeRayDataMenu({ userType: UserType(uData?.companyId, uData?.companyInfo?.org), adminType: uData?.adminType, su })) || (m.suonly && su)));

    return (
        <>
            {uData && <Menu isOpen={menuOpen}
                outerContainerId={"App"}
                id="sidebarPanel"
                pageWrapId={"appCapsule"}
                disableAutoFocus
                onStateChange={(state) => handleStateChange(state)}
                customBurgerIcon={false}
                customCrossIcon={false}
                className={"panelbox"}
                menuClassName={"modal-dialog"}
                itemListClassName={"modal-content"}>
                <div className="modal-body p-0">
                    <div className="profileBox pe-2 pb-2 pt-2 ps-1 bg-primary">
                        <div className="raycur me-1" onClick={closeMN}>
                            <ion-icon name="arrow-back-outline" size="large"></ion-icon>
                        </div>
                        <div className="float-start text-white">
                            <h2 className="text-white">{uData.adminid}</h2>
                            <div className="text-white">{uData.firstName} {uData.lastName}</div>
                            {/* IMP-419: 소속 회사명 SSoT = user.companyInfo (헤더 AdminTopMenu 와 동일 기준) */}
                            {uData?.companyInfo?.name && <div className="text-white">{uData.companyInfo.name}</div>}
                        </div>
                    </div>
                    <div className="listview-title mt-1">{t('common.label.information')}</div>
                    <ul className="listview flush transparent no-line image-listview">
                        {menu1.map((m, idx) =>
                            <li key={idx} className={m.listlink && "lilistlink"}>
                                <a onClick={() => goMenu(m)} className="item">
                                    <div className="icon-box bg-success">
                                        <ion-icon name={m.icon}></ion-icon>
                                    </div>
                                    <div className="in">
                                        {m.title}
                                    </div>
                                </a>
                                {m.listlink && <a onClick={() => goLinkmenu(m)} className="raylinkicon">
                                    <ion-icon name="list-outline"></ion-icon>
                                </a>}
                            </li>
                        )}
                    </ul>
                    {visibleMenu2.length > 0 && <>
                        <div className="listview-title mt-1">{t('common.label.data')}</div>
                        <ul className="listview flush transparent no-line image-listview">
                            {visibleMenu2.map((m, idx) =>
                                <li key={idx}>
                                    <a onClick={() => goMenu(m)} className="item">
                                        <div className="icon-box bg-primary">
                                            <ion-icon name={m.icon}></ion-icon>
                                        </div>
                                        <div className="in">
                                            {m.title}
                                        </div>
                                    </a>
                                </li>
                            )}
                        </ul>
                    </>}
                    <div className="listview-title mt-1">{t('common.label.etc')}</div>
                    <ul className="listview flush transparent no-line image-listview">
                        {/* BUG-101: ExternalDealer 는 tech 포털 접근 불가 — Tech 사이트 버튼 숨김 */}
                        {!isExternalDealer && <li>
                            <a onClick={() => goTech()} className="item">
                                <div className="icon-box bg-secondary">
                                    <ion-icon name="phone-portrait-outline"></ion-icon>
                                </div>
                                <div className="in">
                                    {t('product.label.techView')}
                                </div>
                            </a>
                        </li>}
                        {/* IMP-478 P7a: 외부 딜러 포함 모든 관리자 사용자에게 내 계정 진입점 */}
                        <li>
                            <a onClick={() => goMenu({ link: ROUTES.ADMIN_MY_INFO })} className="item">
                                <div className="icon-box bg-secondary">
                                    <ion-icon name="person-circle-outline"></ion-icon>
                                </div>
                                <div className="in">
                                    {t('user.label.myAccount')}
                                </div>
                            </a>
                        </li>
                        <li>
                            <a onClick={() => Logout()} className="item">
                                <div className="icon-box bg-secondary">
                                    <ion-icon name="log-out-outline"></ion-icon>
                                </div>
                                <div className="in">
                                    {t('auth.button.signOut')}
                                </div>
                            </a>
                        </li>
                    </ul>
                </div>
            </Menu>}
        </>
    );
};

const mapState = (state) => {
    const isOpen = state.ProgramReducer.menuOpen;
    const su = state.AuthReducer.su;
    const groupList = state.SparePartsReducer.groupList;
    // PR #185 R2 — user 변화 시 메뉴 자동 갱신 위해 Redux store 에서 가져옴.
    const user = state.AuthReducer.user;
    return { isOpen, su, groupList, user };
};

const mapDispatch = dispatch => ({
    Logout: () => dispatch(AuthAction.Logout()),
    GetGroupList: () => dispatch(SparePartsAction.GetGroupList())
})

export default connect(mapState, mapDispatch)(AdminLeftMenu);
